import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PartnershipConfig } from '@/types/partnership.types';
import { getPartnership } from '@/data/partnerships';
import { ScoutMark } from '@shared/components/ScoutMark';
import { TimelineChart } from '@/components/TimelineChart';
import { DataStreamsRadar } from '@/components/DataStreamsRadar';
import { DataStreamBars } from '@/components/DataStreamBars';

// Slide wrapper component
interface SlideProps {
  children: React.ReactNode;
}

const Slide: React.FC<SlideProps> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="w-full h-full overflow-y-auto overflow-x-hidden"
      style={{ scrollbarWidth: 'thin' }}
    >
      <div className="w-full max-w-7xl mx-auto min-h-full flex items-center justify-center p-3 sm:p-6 md:p-8 lg:p-12">
        <div className="w-full">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

// Helper to get partner color classes
const usePartnerColors = (config: PartnershipConfig) => {
  return {
    primaryBg: `rgba(${hexToRgb(config.partner.colors.primary)}, 0.1)`,
    primaryBorder: config.partner.colors.primary,
    primaryText: config.partner.colors.primary,
    secondaryBg: `rgba(${hexToRgb(config.partner.colors.secondary)}, 0.05)`,
    secondaryText: config.partner.colors.secondary,
  };
};

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '0, 0, 0';
};

// Slide 1: Title
const TitleSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => (
  <Slide>
    <div className="flex flex-col items-center justify-center text-center px-2 sm:px-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="w-full"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 lg:gap-12 mb-6 sm:mb-12 lg:mb-16">
          <div className="bg-white border-2 border-primary rounded-lg shadow-2xl transform active:scale-95 sm:hover:scale-105 transition-transform w-36 sm:w-56 md:w-64 lg:w-72 aspect-square flex items-center justify-center p-4 sm:px-8 sm:py-14 md:px-10 md:py-16 lg:px-12 lg:py-20">
            <img 
              src="/uploads/Color logo - no background-favicon.svg" 
              alt="Allegory Logo" 
              className="w-full h-auto object-contain"
            />
          </div>
          <div className="text-2xl sm:text-4xl lg:text-6xl font-bold text-foreground">×</div>
          <div 
            className="bg-white border-2 rounded-lg shadow-2xl transform active:scale-95 sm:hover:scale-105 transition-transform w-36 sm:w-56 md:w-64 lg:w-72 aspect-square flex items-center justify-center p-4 sm:p-8 md:p-10 lg:p-12"
            style={{
              borderColor: config.partner.colors.primary,
            }}
          >
            <img 
              src={config.partner.logo} 
              alt={`${config.partner.name} Logo`} 
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        <h1 
          className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-4 sm:mb-8 leading-tight text-primary px-2"
          dangerouslySetInnerHTML={{ __html: config.slides.title.title }}
        />
      </motion.div>
      
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="max-w-4xl px-2"
      >
        <p className="text-base sm:text-xl md:text-2xl lg:text-3xl text-muted-foreground leading-relaxed font-light">
          {config.slides.title.subtitle}
        </p>
      </motion.div>
    </div>
  </Slide>
);

// Slide 2: Opportunity
const OpportunitySlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { opportunity } = config.slides;
  const colors = usePartnerColors(config);
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-6 sm:mb-12 lg:mb-20 text-center px-2">
          The <span className="text-primary">Opportunity</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-8 lg:gap-12">
          <div className="bg-white p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border-2 border-red-500/30 active:border-red-500/50 sm:hover:border-red-500/50 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2">
            <h3 className="text-xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-8 text-red-500">
              {opportunity.challenges.title}
            </h3>
            <div className="space-y-3 sm:space-y-6">
              {opportunity.challenges.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 sm:gap-4 text-sm sm:text-lg lg:text-xl">
                  <span className="text-xl sm:text-3xl flex-shrink-0">{item.icon}</span>
                  <span className="text-muted-foreground mt-0.5 sm:mt-1">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div 
            className="p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border-2 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2"
            style={{
              background: `linear-gradient(to bottom right, ${colors.primaryBg}, transparent)`,
              borderColor: `${config.partner.colors.primary}4D`,
            }}
          >
            <h3 className="text-xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-8" style={{ color: config.partner.colors.primary }}>
              {opportunity.model.title}
            </h3>
            <div className="space-y-3 sm:space-y-6">
              {opportunity.model.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 sm:gap-4 text-sm sm:text-lg lg:text-xl">
                  <span className="text-xl sm:text-3xl flex-shrink-0">{item.icon}</span>
                  <span className="text-muted-foreground mt-0.5 sm:mt-1">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
};

// Slide 3: Strategic Alignment
const AlignmentSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { alignment } = config.slides;
  const colors = usePartnerColors(config);
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="text-center mb-4 sm:mb-6 lg:mb-8">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-2 sm:mb-3 px-2">
          Strategic <span className="text-primary"> Alignment</span>
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">Two organizations, one shared vision</p>
        </div>

        <div className="grid md:grid-cols-2 gap-3 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
          <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border-2 border-primary active:border-primary/70 sm:hover:border-primary/70 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2">
            <div className="text-xs sm:text-sm font-bold text-foreground uppercase tracking-widest mb-2 sm:mb-3">Allegory's Mission</div>
            <div className="text-base sm:text-xl lg:text-2xl font-semibold leading-relaxed">
              {alignment.allegoryMission}
            </div>
          </div>

          <div 
            className="p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border-2 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2"
            style={{
              background: `linear-gradient(to bottom right, ${colors.primaryBg}, ${colors.secondaryBg})`,
              borderColor: config.partner.colors.primary,
            }}
          >
            <div className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-2 sm:mb-3" style={{ color: config.partner.colors.primary }}>
              {config.partner.name}'s Vision
            </div>
            <div className="text-base sm:text-xl lg:text-2xl font-semibold leading-relaxed">
              {alignment.partnerVision}
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border-2 border-primary/30">
          <h3 className="text-xl sm:text-3xl font-bold text-center mb-4 sm:mb-6 text-foreground">Core Principles</h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-6">
            {alignment.sharedPrinciples.map((principle, i) => (
              <div key={i} className="bg-background/50 p-3 sm:p-6 rounded-xl sm:rounded-2xl text-center active:bg-primary/5 sm:hover:bg-primary/5 transition-all sm:hover:-translate-y-1 border border-primary/20 sm:hover:border-primary/40">
                <div className="text-2xl sm:text-4xl lg:text-5xl mb-1 sm:mb-3">{principle.icon}</div>
                <div className="text-xs sm:text-base lg:text-lg font-semibold">{principle.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Slide>
  );
};

// Slide 4: Funding OS
const FundingOSSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { fundingOS } = config.slides;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="text-center mb-6 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-3 sm:mb-6 text-primary px-2">
            Allegory's Funding OS
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">The platform that removes friction from grant funding</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
          {fundingOS.features.map((feature, i) => (
            <div key={i} className="bg-white p-4 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border-2 border-primary/20 border-l-4 border-l-primary active:border-primary sm:hover:border-primary transition-all sm:hover:shadow-2xl sm:hover:translate-x-2">
              <h3 className="text-lg sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-4 flex items-center gap-2 sm:gap-3">
                {feature.isScout ? (
                  <ScoutMark size="lg" variant="inline" className="text-lg sm:text-2xl lg:text-3xl gap-2 sm:gap-3" />
                ) : (
                  <>
                    <span className="text-xl sm:text-3xl lg:text-4xl flex-shrink-0">{feature.icon}</span>
                    <span className="text-primary">{feature.title}</span>
                  </>
                )}
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
};

// Slide 5: Partner Model
const PartnerModelSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { partnerModel } = config.slides;
  const usePartner = partnerModel.usePartnerColors;
  
  // State for interactive data stream values
  const [dataStreamValues, setDataStreamValues] = React.useState(
    partnerModel.dataStreams.map(stream => stream.value ?? 95)
  );

  // Handler for updating individual data stream values
  const handleValueChange = (index: number, value: number) => {
    const newValues = [...dataStreamValues];
    newValues[index] = value;
    setDataStreamValues(newValues);
  };

  // Create updated data streams with current values
  const updatedDataStreams = partnerModel.dataStreams.map((stream, index) => ({
    ...stream,
    value: dataStreamValues[index],
  }));
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="text-center mb-3 sm:mb-4 lg:mb-6">
          <h2 
            className="text-xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-2 sm:mb-3 px-2"
            style={usePartner ? { color: config.partner.colors.primary } : {}}
          >
            <span className={!usePartner ? 'text-primary' : ''}>{partnerModel.title}</span>
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">{partnerModel.subtitle}</p>
        </div>
        
        {/* Main container */}
        <div className="space-y-3 sm:space-y-5 lg:space-y-6">
          {/* Side-by-side Radar + Bars with same spacing as slide 2 */}
          <div className="grid md:grid-cols-2 gap-3 sm:gap-5 lg:gap-6">
            {/* Left: Radar Chart */}
            <div 
              className={`p-4 sm:p-5 lg:p-6 rounded-2xl sm:rounded-3xl border-2 bg-white flex flex-col ${!usePartner ? 'border-primary/30' : ''}`}
              style={usePartner ? {
                borderColor: `${config.partner.colors.primary}4D`,
              } : {}}
            >
              <h3 
                className={`text-base sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-4 ${!usePartner ? 'text-primary' : ''}`}
                style={usePartner ? { color: config.partner.colors.primary } : {}}
              >
                Five Data Streams
              </h3>
              <div className="flex-1 flex items-center justify-center min-h-[200px] sm:min-h-[260px]">
                <DataStreamsRadar 
                  dataStreams={updatedDataStreams}
                  partnerPrimaryColor={usePartner ? config.partner.colors.primary : '#B100FF'}
                  partnerSecondaryColor={usePartner ? config.partner.colors.secondary : undefined}
                />
              </div>
            </div>

            {/* Right: Data Stream Bars */}
            <div 
              className={`p-4 sm:p-5 lg:p-6 rounded-2xl sm:rounded-3xl border-2 bg-white ${!usePartner ? 'border-primary/30' : ''}`}
              style={usePartner ? {
                borderColor: `${config.partner.colors.primary}4D`,
              } : {}}
            >
              <h3 
                className={`text-base sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-4 ${!usePartner ? 'text-primary' : ''}`}
                style={usePartner ? { color: config.partner.colors.primary } : {}}
              >
                Assessment Scores
              </h3>
              <DataStreamBars 
                dataStreams={updatedDataStreams}
                partnerPrimaryColor={usePartner ? config.partner.colors.primary : '#B100FF'}
                editable={true}
                onChange={handleValueChange}
              />
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
};

// Slide 6: Phase 1
const Phase1Slide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { phase1 } = config.slides;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl mb-5 sm:mb-8 lg:mb-12 text-center border-2 border-primary shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent" />
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-2 sm:mb-4 relative z-10">{phase1.title}</h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-foreground relative z-10">{phase1.dateRange}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 sm:gap-6 lg:gap-8 mb-5 sm:mb-8 lg:mb-10">
          {phase1.deliverables.map((deliverable, i) => (
            <div key={i} className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border-2 border-primary active:border-primary/70 sm:hover:border-primary/70 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary" />
              {deliverable.cost && (
                <div className="text-xl sm:text-3xl lg:text-4xl font-extrabold text-primary mb-2 sm:mb-4">{deliverable.cost}</div>
              )}
              <h3 className="text-sm sm:text-lg lg:text-xl font-bold mb-3 sm:mb-6">{deliverable.title}</h3>
              <ul className="space-y-2">
                {deliverable.features.map((feature, j) => (
                  <li key={j} className="text-xs sm:text-sm text-muted-foreground border-b border-primary/10 pb-2 last:border-0">{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl border-2 border-primary/30">
          <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:gap-8 text-center">
            {phase1.metrics.timeline && (
              <div>
                <div className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2 uppercase tracking-wider">Timeline</div>
                <div className="text-lg sm:text-3xl lg:text-4xl font-extrabold text-foreground">{phase1.metrics.timeline}</div>
              </div>
            )}
            {phase1.metrics.successMetric && (
              <div>
                <div className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2 uppercase tracking-wider">Success Metric</div>
                <div className="text-lg sm:text-3xl lg:text-4xl font-extrabold text-foreground">{phase1.metrics.successMetric}</div>
              </div>
            )}
            {phase1.metrics.cost && (
              <div className="col-span-2">
                <div className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2 uppercase tracking-wider">Cost</div>
                <div className="text-lg sm:text-3xl lg:text-4xl font-extrabold text-foreground">{phase1.metrics.cost}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Slide>
  );
};

// Slide 7: Timeline with React Flow
const TimelineSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { timeline } = config.slides;
  if (!timeline) return null;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center h-full">
        <div className="text-center mb-4 sm:mb-8">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2 sm:mb-4 text-primary px-2">
            {timeline.title}
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">{timeline.subtitle}</p>
        </div>

        {/* Timeline Chart */}
        <div className="flex-1 w-full min-h-[300px] sm:min-h-[400px]">
          <TimelineChart 
            items={timeline.items} 
            partnerPrimaryColor={config.partner.colors.primary}
            milestoneTitle={timeline.milestoneTitle}
            milestoneDescription={timeline.milestoneDescription}
          />
        </div>
      </div>
    </Slide>
  );
};

// Slide 8: Phase 2
const Phase2Slide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { phase2 } = config.slides;
  if (!phase2) return null;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="text-center mb-5 sm:mb-8 lg:mb-12">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2 sm:mb-4 text-primary px-2">
            {phase2.title}
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">{phase2.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-3 sm:gap-6 lg:gap-8 mb-5 sm:mb-12">
          {phase2.years.map((year, i) => (
            <div key={i} className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-3xl border-2 border-primary/20 active:border-primary sm:hover:border-primary transition-all sm:hover:shadow-xl sm:hover:-translate-y-2">
              <div className="text-xs sm:text-sm font-bold text-primary uppercase tracking-widest mb-2">{year.label}</div>
              <h3 className="text-base sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-6">{year.title}</h3>
              <ul className="space-y-2 sm:space-y-3">
                {year.activities.map((activity, j) => (
                  <li key={j} className="flex items-start gap-2 text-xs sm:text-sm lg:text-base text-muted-foreground">
                    <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                    <span>{activity}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {phase2.showTotalValue && phase2.totalValue && (
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-5 sm:p-8 rounded-2xl sm:rounded-3xl text-center border-2 border-primary shadow-2xl">
            <div className="text-2xl sm:text-4xl lg:text-6xl font-extrabold mb-2">{phase2.totalValue}</div>
            <div className="text-sm sm:text-lg lg:text-xl opacity-90">Total Partnership Value</div>
          </div>
        )}
      </div>
    </Slide>
  );
};

// Slide 9: Benefits (Partner)
const BenefitsPartnerSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { benefitsPartner } = config.slides;
  if (!benefitsPartner) return null;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="text-center mb-5 sm:mb-8 lg:mb-12">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2 sm:mb-4 px-2" style={benefitsPartner.usePartnerColors ? { color: config.partner.colors.primary } : {}}>
            {benefitsPartner.title}
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">{benefitsPartner.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {benefitsPartner.benefits.map((benefit, i) => (
            <div 
              key={i} 
              className="p-4 sm:p-8 rounded-2xl sm:rounded-3xl border-2 transition-all sm:hover:shadow-xl sm:hover:-translate-y-2 bg-white"
              style={benefitsPartner.usePartnerColors ? {
                borderColor: `${config.partner.colors.primary}33`,
                borderLeftWidth: '4px',
                borderLeftColor: config.partner.colors.secondary,
              } : {}}
            >
              <div className="text-2xl sm:text-4xl mb-2 sm:mb-4">{benefit.icon}</div>
              <h3 className="text-base sm:text-xl font-bold mb-2 sm:mb-3">{benefit.title}</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
};

// Slide 10: Benefits (Allegory)
const BenefitsAllegorySlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { benefitsAllegory } = config.slides;
  if (!benefitsAllegory) return null;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <div className="text-center mb-5 sm:mb-8 lg:mb-12">
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2 sm:mb-4 text-primary px-2">
            {benefitsAllegory.title}
          </h2>
          <p className="text-sm sm:text-xl lg:text-2xl text-muted-foreground px-2">{benefitsAllegory.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {benefitsAllegory.benefits.map((benefit, i) => (
            <div key={i} className="bg-white p-4 sm:p-8 rounded-2xl sm:rounded-3xl border-2 border-primary/20 border-l-4 border-l-primary active:border-primary sm:hover:border-primary transition-all sm:hover:shadow-xl sm:hover:-translate-y-2">
              <div className="text-2xl sm:text-4xl mb-2 sm:mb-4">{benefit.icon}</div>
              <h3 className="text-base sm:text-xl font-bold mb-2 sm:mb-3 text-primary">{benefit.title}</h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
};

// Slide 11: Why This Works
const WhyWorksSlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { whyWorks } = config.slides;
  if (!whyWorks) return null;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center">
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-center mb-6 sm:mb-12 lg:mb-16 text-primary px-2">
          Why This <span className="text-foreground">Partnership Works</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-8 lg:gap-12 mb-6 sm:mb-12 lg:mb-16">
          <div 
            className="p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border-2 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2 bg-white"
            style={{
              borderColor: config.partner.colors.primary,
            }}
          >
            <h3 className="text-xl sm:text-3xl font-bold mb-3 sm:mb-6" style={{ color: config.partner.colors.primary }}>
              {config.partner.name} Brings
            </h3>
            <ul className="space-y-2 sm:space-y-4">
              {whyWorks.partnerBrings.map((item, i) => (
                <li key={i} className="flex items-start gap-2 sm:gap-3">
                  <span className="text-base sm:text-xl flex-shrink-0" style={{ color: config.partner.colors.secondary }}>✓</span>
                  <span className="text-sm sm:text-base lg:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border-2 border-primary active:border-primary/70 sm:hover:border-primary/70 transition-all sm:hover:shadow-2xl sm:hover:-translate-y-2">
            <h3 className="text-xl sm:text-3xl font-bold mb-3 sm:mb-6 text-primary">Allegory Brings</h3>
            <ul className="space-y-2 sm:space-y-4">
              {whyWorks.allegoryBrings.map((item, i) => (
                <li key={i} className="flex items-start gap-2 sm:gap-3">
                  <span className="text-primary text-base sm:text-xl flex-shrink-0">✓</span>
                  <span className="text-sm sm:text-base lg:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-5 sm:p-8 lg:p-12 rounded-2xl sm:rounded-3xl text-center border-2 border-primary shadow-2xl">
          <h3 className="text-xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-4">Together</h3>
          <p className="text-sm sm:text-lg lg:text-2xl font-light leading-relaxed">{whyWorks.togetherStatement}</p>
        </div>
      </div>
    </Slide>
  );
};

// Slide 12: CTA
const CTASlide: React.FC<{ config: PartnershipConfig }> = ({ config }) => {
  const { cta } = config.slides;
  if (!cta) return null;
  
  return (
    <Slide>
      <div className="flex flex-col justify-center items-center text-center">
        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-4 sm:mb-6 lg:mb-8 text-primary px-2">
          {cta.title}
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6 lg:mb-8 w-full max-w-6xl">
          {cta.nextSteps.map((step, i) => (
            <div key={i} className="bg-white p-4 sm:p-5 lg:p-6 rounded-2xl sm:rounded-3xl border-2 border-primary/20 active:border-primary sm:hover:border-primary transition-all sm:hover:shadow-xl sm:hover:-translate-y-2">
              <div className="text-3xl sm:text-5xl mb-2 sm:mb-3">{step.icon}</div>
              <div className="text-xs sm:text-base font-bold text-primary mb-1 sm:mb-2 uppercase tracking-wider">{step.step}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">{step.desc}</div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl max-w-4xl border-2 border-primary shadow-2xl">
          <p className="text-base sm:text-xl lg:text-2xl xl:text-3xl font-light leading-relaxed">
            {cta.closingStatement}
          </p>
        </div>
      </div>
    </Slide>
  );
};

const PartnershipPresentation: React.FC = () => {
  const { partnerId = 'ashoka' } = useParams<{ partnerId: string }>();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Load partnership config
  const config = getPartnership(partnerId);
  
  // Reset currentSlide when partnerId changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [partnerId]);

  // Build enabled slides array and calculate totalSlides first
  const slideComponents: Record<string, React.ReactNode> = {
    title: config?.slides.title.enabled ? <TitleSlide key="title" config={config} /> : null,
    opportunity: config?.slides.opportunity.enabled ? <OpportunitySlide key="opportunity" config={config} /> : null,
    alignment: config?.slides.alignment.enabled ? <AlignmentSlide key="alignment" config={config} /> : null,
    fundingOS: config?.slides.fundingOS.enabled ? <FundingOSSlide key="fundingOS" config={config} /> : null,
    partnerModel: config?.slides.partnerModel.enabled ? <PartnerModelSlide key="partnerModel" config={config} /> : null,
    phase1: config?.slides.phase1.enabled ? <Phase1Slide key="phase1" config={config} /> : null,
    timeline: config?.slides.timeline?.enabled ? <TimelineSlide key="timeline" config={config} /> : null,
    phase2: config?.slides.phase2?.enabled ? <Phase2Slide key="phase2" config={config} /> : null,
    benefitsPartner: config?.slides.benefitsPartner?.enabled ? <BenefitsPartnerSlide key="benefitsPartner" config={config} /> : null,
    benefitsAllegory: config?.slides.benefitsAllegory?.enabled ? <BenefitsAllegorySlide key="benefitsAllegory" config={config} /> : null,
    whyWorks: config?.slides.whyWorks?.enabled ? <WhyWorksSlide key="whyWorks" config={config} /> : null,
    cta: config?.slides.cta?.enabled ? <CTASlide key="cta" config={config} /> : null,
  };

  // Use custom slideOrder if provided, otherwise use default order
  const defaultOrder = ['title', 'opportunity', 'alignment', 'fundingOS', 'partnerModel', 'phase1', 'timeline', 'phase2', 'benefitsPartner', 'benefitsAllegory', 'whyWorks', 'cta'];
  const slideOrder = config?.slideOrder || defaultOrder;

  // Build enabled slides array in the specified order
  const enabledSlides = slideOrder
    .filter(key => slideComponents[key] !== null)
    .map(key => ({ key, component: slideComponents[key] }));
  
  const totalSlides = enabledSlides.length;

  // Navigation callbacks - must be defined before any early returns
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev < totalSlides - 1 ? prev + 1 : prev));
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev > 0 ? prev - 1 : prev));
  }, []);

  // Keyboard navigation - must be defined before any early returns
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  
  if (!config) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Partnership Not Found</h1>
          <p className="text-muted-foreground">The partnership "{partnerId}" does not exist.</p>
        </div>
      </div>
    );
  }

  const progress = ((currentSlide + 1) / totalSlides) * 100;

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-gradient-to-br from-white via-secondary/15 to-primary/15 font-manrope flex flex-col">
      {/* Progress bar */}
      <div className="w-full h-1 bg-muted z-50">
        <motion.div
          className="h-full bg-gradient-to-r from-primary to-primary"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Navigation controls - sticky at top with better mobile touch targets */}
      <div className="sticky top-0 z-40 w-full h-[56px] sm:h-[72px] flex items-center justify-between px-3 sm:px-6 lg:px-8 bg-white/80 backdrop-blur-sm border-b border-border/50">
        <Button
          variant="outline"
          size="lg"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="gap-1 sm:gap-2 border-primary hover:bg-primary hover:text-white disabled:opacity-30 text-xs sm:text-base px-3 py-2 sm:px-4 sm:py-3 min-h-[44px] touch-manipulation"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Prev</span>
        </Button>

        <div className="text-xs sm:text-base lg:text-lg font-semibold text-foreground tracking-wider">
          {currentSlide + 1} of {totalSlides}
        </div>

        <Button
          variant="outline"
          size="lg"
          onClick={nextSlide}
          disabled={currentSlide === totalSlides - 1}
          className="gap-1 sm:gap-2 border-primary hover:bg-primary hover:text-white disabled:opacity-30 text-xs sm:text-base px-3 py-2 sm:px-4 sm:py-3 min-h-[44px] touch-manipulation"
        >
          <span>Next</span>
          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </Button>
      </div>

      {/* Slides container */}
      <div className="relative flex-1 w-full overflow-hidden">
        <AnimatePresence mode="wait">
          {enabledSlides[currentSlide]?.component}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PartnershipPresentation;
